import React, { FC, Fragment } from 'react';
import { styled } from '@mui/material/styles';
import usePageTitle from 'hooks/usePageTitle';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import { ThemeButton } from 'DesignSystem';
import { Box } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { EmptyStateCard } from 'DesignSystem';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { ImpactClaim, ImpactClaimStatus } from 'FeatureModules/Request/types/impactClaim.types';
import { Trash } from '@styled-icons/bootstrap';
import { Edit } from '@styled-icons/boxicons-solid';
import { useNavigate } from 'react-router-dom';
import useImpactClaimActions from 'FeatureModules/ImpactClaim/hooks/useImpactClaimActions';
import useImpactClaims from 'FeatureModules/Request/hooks/useImpactClaims';
import ImpactClaimTable from '../Components/Table/ImpactClaimTable';
import ImpactClaimSkeleton from '../Components/Table/ImpactClaimSkeleton';
import ErrorState from 'Components/ErrorState';
import EmptySearchResults from 'Components/EmptySearchResults';
import PageContainer from 'Components/PageContainer';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import ActionContainer from 'Components/ActionContainer';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

interface ImpactClaimsProps {
  status?: ImpactClaimStatus[];
}

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,
  marginTop: 16,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const ImpactClaims: FC<ImpactClaimsProps> = ({ status }) => {
  usePageTitle('Impact Claims');
  const navigate = useNavigate();
  const { handleDelete, handleEdit, handleAdd } = useImpactClaimActions();

  const {
    impactClaims,
    loading,
    error,
    searchTerm,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
  } = useImpactClaims({
    status,
  });

  const handleClickClaim = (claimId: string) => {
    navigate(`/impact-claims/active/${claimId}`);
  };

  if (error) return <ErrorState />;

  return (
    <PageContainer>
      {!impactClaims?.length && !searchTerm?.length && !loading ? (
        <Box mt={4}>
          <EmptyStateCard
            title="Add your claims to demonstrate and prove your social and environmental impact"
            size={EAvailableSizes.LARGE}
            data-testid="claims-getting-started-card"
            background={EBackgroundTypes.WORLD}
            variant={EItemTypes.IMPACT_CLAIM}
            buttonText="Add claim"
            onCreateItemClick={handleAdd}
            data-testid-button="claims-getting-started-create-btn"
          />
        </Box>
      ) : (
        <Fragment>
          <Box>
            <Container>
              <ActionContainer>
                <SearchToolbar
                  setSearchTerm={handleSearchTermChange}
                  searchTerm={searchTerm || ''}
                  className="search-input"
                  data-testid="impact-claim-search-input"
                  placeholder="Search product"
                  actions={
                    <Box ml={1}>
                      <ThemeButton
                        size="medium"
                        startIcon={<Plus size={20} color="white" />}
                        data-testid="create-impact-claim-btn"
                        color="PRIMARY"
                        onClick={handleAdd}
                      >
                        Add new claim
                      </ThemeButton>
                    </Box>
                  }
                />
              </ActionContainer>
              {loading && (
                <Box position="relative" minHeight={400}>
                  <Loader />
                </Box>
              )}

              {!loading && !!impactClaims.length && (
                <ScrollableTableContainer pb={1}>
                  <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
                    <ImpactClaimTable
                      impactClaims={impactClaims}
                      actions={[
                        {
                          icon: <Edit size="17" />,
                          tooltip: 'Edit impact claim',
                          onClick: (impactClaim: ImpactClaim) => handleEdit(impactClaim),
                        },
                        {
                          tooltip: 'Delete impact claim',
                          icon: <Trash size="17" />,
                          onClick: (impactClaim: ImpactClaim) => handleDelete(impactClaim.id),
                        },
                      ]}
                      onSelect={handleClickClaim}
                    />
                  </InfiniteScrollWrapper>
                </ScrollableTableContainer>
              )}
            </Container>
          </Box>
          {searchTerm && !impactClaims.length && !loading && (
            <EmptySearchResults
              message="No claim matches your search"
              action={{
                onClick: () => undefined,
                label: 'Create new',
              }}
              skeletonComponent={<ImpactClaimSkeleton />}
            />
          )}
        </Fragment>
      )}
    </PageContainer>
  );
};

export default ImpactClaims;
